/**
 * clasificadores/postventa.js — Evaluador determinístico del dashboard Postventa ML
 *
 * Patrón: función pura. Recibe datos crudos de Firestore + umbrales del agente,
 * devuelve secciones clasificadas con items accionables. Sigue el mismo shape
 * que clasificadores/publicidad.js y clasificadores/opiniones.js.
 *
 * Las reglas son determinísticas (no usan IA). El tab Patrones del dashboard
 * sí usa IA — eso es otro flujo independiente.
 *
 * Input:
 *   - reclamos: array de mlReclamos
 *   - devoluciones: array de mlDevoluciones
 *   - ordenes: array de mlOrdenes (últimos N días)
 *   - envios: array de mlEnvios (últimos N días)
 *   - publicaciones: array de mlPublicaciones
 *   - umbrales: { [seccion]: { tolerancia_pct?, volumen_minimo?, ventana_dias? } }
 *       — viene del campo `umbrales` del agente. Defaults dentro de cada regla.
 *
 * Output:
 *   {
 *     analisis: {
 *       totalReclamos, totalDevoluciones, totalVentas,
 *       promedioCuenta: { pausar, proveedor, embalaje },
 *     },
 *     secciones: {
 *       modificar_publicacion: [items],
 *       pausar_publicacion: [items],
 *       cambiar_transportista: [items],
 *       cambiar_proveedor: [items],
 *       cambiar_embalaje: [items],
 *     },
 *   }
 *
 * Reglas (`docs/postventa-mvp-plan.md` §F6 — confirmadas con Mariano 17/may):
 *   - modificar_publicacion: TODAS las pubs con ≥1 reclamo y volumen mínimo
 *     de ventas. No tiene tolerancia (va siempre — es aprendizaje continuo).
 *   - pausar_publicacion: tasa_reclamo_pub > promedio_cuenta × (1 + tolerancia/100).
 *   - cambiar_proveedor: tasa_defecto_pub > promedio_cuenta × (1 + tolerancia/100).
 *   - cambiar_embalaje: tasa_damaged_pub > promedio_cuenta × (1 + tolerancia/100).
 *   - cambiar_transportista: análogo, agrupado por transportista (no por pub).
 *
 * Mariano puede ajustar la tolerancia por sección desde la UI del agente.
 */

// ═══════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════

/**
 * Resuelve el itemId (publicación ML) de un reclamo.
 *
 * Orden de resolución:
 *   1. `claim.itemId` ← persistido por el adapter `reclamos.js` post-enrich
 *      (vía fetchClaim → related_entities). Ese es el camino canónico.
 *   2. Fallback para claims viejos sin enriquecer aún: cascada
 *      `resourceId/order_id → mlOrdenes → order_items[0].item.id`.
 *   3. Fallback adicional: parsear `resource: '/orders/XXX'`.
 *
 * Devuelve null si ninguna funciona. El próximo cron de reclamos
 * va a enriquecer ese claim y la siguiente pasada lo resuelve.
 *
 * Exportada para reusar desde el dashboard al armar "Publicaciones afectadas".
 */
export function itemIdDeReclamo(reclamo, ordenesPorId) {
  // Vía 1 (canónica post-enrich): el campo `itemId` ya viene persistido en mlReclamos.
  if (reclamo.itemId) return String(reclamo.itemId)

  // Vía 2 (fallback legacy): related_entities en el shape original de ML.
  const directItemId = reclamo.related_entities?.item_id
    || reclamo.relatedEntities?.item_id
    || reclamo.item_id
    || null
  if (directItemId) return String(directItemId)

  // Vía 3 (fallback): orderId → mlOrdenes → itemId.
  // El claim referencia la orden por `resourceId` (cuando `resource === 'order'`)
  // o por `orderId` (post-enrich). La orden en `mlOrdenes` tiene shape MAPEADO
  // por el adapter: `orderItems[].itemId` (camelCase), NO el shape crudo de ML
  // `order_items[].item.id`. Buscamos ambos por compatibilidad con docs viejos.
  let orderId = reclamo.orderId || reclamo.resourceId || reclamo.order_id || null
  if (!orderId && typeof reclamo.resource === 'string') {
    const m = reclamo.resource.match(/\/orders\/(\d+)/)
    if (m) orderId = m[1]
  }

  if (!orderId) return null
  const orden = ordenesPorId[String(orderId)]
  if (!orden) return null
  // Shape mapeado por el adapter (lo persistido en mlOrdenes hoy).
  const itemIdMapeado = orden.orderItems?.[0]?.itemId
  if (itemIdMapeado) return String(itemIdMapeado)
  // Shape raw de ML (fallback por si quedan docs viejos sin remapear).
  const itemIdRaw = orden.order_items?.[0]?.item?.id
  if (itemIdRaw) return String(itemIdRaw)
  return null
}

/** Indexa órdenes por id para lookup rápido. Exportada para reuso.
 *  Indexa por TODOS los identificadores que pueda traer la orden — `id`
 *  (documentId de Firestore vía useDashboardData) y `ordenId` (campo
 *  persistido por el adapter). Algunos códigos buscan por uno, otros por el otro. */
export function buildOrdenesPorId(ordenes) {
  const m = {}
  for (const o of ordenes) {
    if (o.id != null) m[String(o.id)] = o
    if (o.ordenId != null) m[String(o.ordenId)] = o
  }
  return m
}

/** Mapa de ventas por publicación (cantidad de unidades vendidas en la ventana).
 * Fallback: si no hay quantity en order_items, contamos 1 por orden (no 0). */
function buildVentasPorPub(ordenes) {
  const m = {}
  for (const o of ordenes) {
    for (const oi of (o.order_items || [])) {
      const itemId = oi.item?.id
      if (!itemId) continue
      if (!m[itemId]) m[itemId] = 0
      const qty = Number(oi.quantity) || 0
      m[itemId] += qty > 0 ? qty : 1
    }
  }
  return m
}

/** Mapa de reclamos por publicación (resuelto via orden). */
function buildReclamosPorPub(reclamos, ordenesPorId) {
  const m = {}
  for (const r of reclamos) {
    const itemId = itemIdDeReclamo(r, ordenesPorId)
    if (!itemId) continue
    if (!m[itemId]) m[itemId] = []
    m[itemId].push(r)
  }
  return m
}

/**
 * Categoriza un motivo de reclamo/devolución de ML.
 * ML devuelve los motivos en español ("Producto incompleto", "El comprador
 * se arrepintió", "Producto no compatible con el vehículo", etc).
 * Esta función devuelve un set de categorías que aplican al motivo.
 *
 * Categorías:
 *   - defecto: producto roto, defectuoso, incompleto, no funcional.
 *   - damaged: producto llegó dañado/roto físicamente.
 *   - no_compatible: producto no compatible con lo que esperaba el comprador.
 *   - arrepentimiento: comprador cambió de idea sin culpa del seller.
 *   - diferente: producto entregado distinto al pedido (control de calidad).
 *   - envio: problemas de logística/transportista (no llegó, demoró, etc).
 */
function categorizarMotivo(rawReason) {
  const reason = (rawReason || '').toString().toLowerCase()
  if (!reason) return new Set()
  const cats = new Set()
  // damaged: prioridad alta — implica embalaje/transporte/manipulación
  if (reason.includes('damaged') || reason.includes('dañ') || reason.includes('roto') || reason.includes('broken')) {
    cats.add('damaged')
  }
  // defecto: incluye incompleto (falta una pieza = defecto de control)
  if (reason.includes('defecto') || reason.includes('defective') || reason.includes('failure')
      || reason.includes('incompleto') || reason.includes('incomplete')
      || reason.includes('no funciona') || reason.includes('not working')) {
    cats.add('defecto')
  }
  // no compatible (autopartes, talles, etc)
  if (reason.includes('no compatible') || reason.includes('incompatible') || reason.includes('not compatible')
      || (reason.includes('compatible') && reason.includes('vehíc'))) {
    cats.add('no_compatible')
  }
  // arrepentimiento (sin culpa del seller — útil para entender qué publicaciones engañan)
  if (reason.includes('arrepin') || reason.includes('arrepent') || reason.includes('regret')
      || reason.includes('cambió de idea') || reason.includes('cambio de idea')) {
    cats.add('arrepentimiento')
  }
  // producto diferente al pedido (control de calidad, foto vs producto real)
  if (reason.includes('diferente') || reason.includes('different')) {
    cats.add('diferente')
  }
  // logística / envío
  if (reason.includes('envío') || reason.includes('envio') || reason.includes('shipping')
      || reason.includes('no recibió') || reason.includes('no recibido') || reason.includes('no le llegó')
      || reason.includes('no llegó') || reason.includes('demoró') || reason.includes('demoro')
      || reason.includes('aparece entregado')) {
    cats.add('envio')
  }
  return cats
}

/**
 * Verifica si un reclamo está abierto (vs cerrado/resuelto).
 * ML: status='opened' o stage='claim' generalmente indica abierto.
 */
function reclamoAbierto(r) {
  if (!r) return false
  if (r.status === 'closed' || r.status === 'cerrado' || r.status === 'resolved') return false
  if (r.fulfilled === true) return false
  return true
}

/** Verifica si una devolución está abierta (no finalizada). */
function devolucionAbierta(d) {
  if (!d) return false
  if (d.status === 'closed' || d.status === 'finished' || d.status === 'finalizado') return false
  if (d.dateClosed) return false
  return true
}

/**
 * Mapa de devoluciones por publicación, categorizadas por motivo.
 * Las devoluciones se cruzan con su reclamo asociado (claimId) → orden → item.
 */
function buildDevolucionesPorPub(devoluciones, reclamos, ordenesPorId) {
  const reclamosPorClaimId = {}
  for (const r of reclamos) reclamosPorClaimId[String(r.claimId)] = r

  const m = {}
  for (const dev of devoluciones) {
    const reclamo = reclamosPorClaimId[String(dev.claimId)]
    if (!reclamo) continue
    const itemId = itemIdDeReclamo(reclamo, ordenesPorId)
    if (!itemId) continue
    if (!m[itemId]) m[itemId] = {
      todas: [], defecto: [], damaged: [], no_compatible: [],
      arrepentimiento: [], diferente: [], envio: [],
    }
    m[itemId].todas.push(dev)
    // El motivo puede venir del return o del reclamo asociado — probamos ambos.
    const cats = categorizarMotivo(dev.returnReason || dev.return_reason)
    const catsReclamo = categorizarMotivo(reclamo.reasonId || reclamo.motivo)
    for (const c of cats) m[itemId][c]?.push(dev)
    for (const c of catsReclamo) if (!cats.has(c)) m[itemId][c]?.push(dev)
  }
  return m
}

/** Item base con metadatos de la publicación (para mostrar en el dashboard). */
function buildItem(itemId, pubsMap, extras = {}) {
  const pub = pubsMap[itemId] || {}
  return {
    id: itemId,
    _itemId: itemId,
    _titulo: pub.title || itemId,
    _thumbnail: pub.thumbnail || pub.pictures?.[0]?.url || null,
    _precio: pub.price || null,
    _permalink: pub.permalink || (itemId ? `https://articulo.mercadolibre.com.ar/${itemId.replace(/^(MLA)(\d+)$/, '$1-$2')}` : null),
    _status: pub.status || null,
    ...extras,
  }
}

// ═══════════════════════════════════════════════════════════════
// Función principal: clasificarPostventa
// ═══════════════════════════════════════════════════════════════

export function clasificarPostventa({
  reclamos = [],
  devoluciones = [],
  ordenes = [],
  envios = [],     // futuro: análisis de transportista
  publicaciones = [],
  umbrales = {},
  mapeoReason = {}, // reasonId → categoría (mismo mapa que el dashboard)
  fechaCorteReclamos = null, // ms — solo reclamos desde acá cuentan en "otras"
  dismissedCasos = {}, // { 'otros:itemId': {...} } — situaciones descartadas por el operador
  mlClaimReasons = {}, // reasonId → texto del motivo
} = {}) {
  const pubsMap = {}
  for (const p of publicaciones) pubsMap[p.id] = p

  const ordenesPorId = buildOrdenesPorId(ordenes)
  const ventasPorPub = buildVentasPorPub(ordenes)
  // Map con TODOS los reclamos (para "Modificar publicación" — aprendizaje).
  const reclamosPorPub = buildReclamosPorPub(reclamos, ordenesPorId)
  // Map con solo reclamos abiertos (para "Pausar publicación" — acción urgente).
  const reclamosAbiertosPorPub = buildReclamosPorPub(reclamos.filter(reclamoAbierto), ordenesPorId)
  // Map con devoluciones (incluye solo abiertas para "Cambiar proveedor/embalaje").
  const devolucionesPorPub = buildDevolucionesPorPub(devoluciones, reclamos, ordenesPorId)
  const devolucionesAbiertasPorPub = buildDevolucionesPorPub(
    devoluciones.filter(devolucionAbierta), reclamos, ordenesPorId
  )

  // Totales para calcular promedios de cuenta
  const ventasTotal = Object.values(ventasPorPub).reduce((s, v) => s + v, 0)
  const reclamosTotalCount = reclamos.length
  const totalDefectoCuenta = Object.values(devolucionesAbiertasPorPub).reduce((s, v) => s + v.defecto.length, 0)
  const totalDevolucionesCount = devoluciones.length
  const totalDamagedCuenta = Object.values(devolucionesAbiertasPorPub).reduce((s, v) => s + v.damaged.length, 0)

  // Promedios de cuenta
  const promCuenta_pausar = ventasTotal > 0 ? reclamosTotalCount / ventasTotal : 0
  const promCuenta_proveedor = ventasTotal > 0 ? totalDefectoCuenta / ventasTotal : 0
  const promCuenta_embalaje = totalDevolucionesCount > 0 ? totalDamagedCuenta / totalDevolucionesCount : 0

  // ─── SECCIÓN 1 — Modificar publicación (sin tolerancia, va siempre) ───
  const u_mod = umbrales.modificar_publicacion || {}
  const volumenMin_mod = Number(u_mod.volumen_minimo_ventas) || 5

  const seccionModificar = []
  for (const [itemId, recs] of Object.entries(reclamosPorPub)) {
    const ventas = ventasPorPub[itemId] || 0
    if (ventas < volumenMin_mod) continue
    seccionModificar.push(buildItem(itemId, pubsMap, {
      _ventas: ventas,
      _reclamos: recs.length,
      _tasaReclamo: ventas > 0 ? recs.length / ventas : 0,
      metricas: { ventas, reclamos: recs.length },
    }))
  }
  // Ordenar por cantidad de reclamos descendente (más problemáticos primero)
  seccionModificar.sort((a, b) => (b._reclamos || 0) - (a._reclamos || 0))

  // ─── SECCIÓN 2 — Pausar publicación (tasa > promedio × (1 + tolerancia)) ───
  // Usa SOLO reclamos abiertos (acción urgente — no tiene sentido pausar
  // por reclamos ya resueltos).
  const u_pausar = umbrales.pausar_publicacion || {}
  const tolerancia_pausar = Number(u_pausar.tolerancia_pct) || 20
  const volumenMin_pausar = Number(u_pausar.volumen_minimo_ventas) || 10
  const reclamosAbiertosTotal = Object.values(reclamosAbiertosPorPub).reduce((s, v) => s + v.length, 0)
  const promCuenta_pausarAbierto = ventasTotal > 0 ? reclamosAbiertosTotal / ventasTotal : 0
  const umbralPausar = promCuenta_pausarAbierto * (1 + tolerancia_pausar / 100)

  const seccionPausar = []
  for (const [itemId, recs] of Object.entries(reclamosAbiertosPorPub)) {
    const ventas = ventasPorPub[itemId] || 0
    if (ventas < volumenMin_pausar) continue
    const tasa = recs.length / ventas
    if (tasa > umbralPausar) {
      seccionPausar.push(buildItem(itemId, pubsMap, {
        _ventas: ventas,
        _reclamos: recs.length,
        _tasaReclamo: tasa,
        _promedioCuenta: promCuenta_pausarAbierto,
        _umbral: umbralPausar,
        _excesoSobrePromedio: promCuenta_pausarAbierto > 0 ? (tasa / promCuenta_pausarAbierto - 1) : null,
        metricas: { ventas, reclamos: recs.length, tasa, umbral: umbralPausar },
      }))
    }
  }
  seccionPausar.sort((a, b) => (b._tasaReclamo || 0) - (a._tasaReclamo || 0))

  // ─── SECCIÓN 3 — Cambiar transportista ───
  // Pendiente: requiere análisis de mlEnvios agrupado por transportista × zona.
  // Devuelve [] por ahora — la sección existe en el dashboard pero sin items
  // hasta que se implemente el análisis. Bloque 2.5 (no F6 base).
  const seccionTransportista = []

  // ─── SECCIÓN 4 — Cambiar proveedor (tasa defecto > promedio × (1+tol)) ───
  const u_prov = umbrales.cambiar_proveedor || {}
  const tolerancia_prov = Number(u_prov.tolerancia_pct) || 20
  const volumenMin_prov = Number(u_prov.volumen_minimo_ventas) || 30
  const umbralProveedor = promCuenta_proveedor * (1 + tolerancia_prov / 100)

  const seccionProveedor = []
  // "Defecto" incluye motivos: defectuoso, falla, incompleto, no funciona, producto diferente.
  for (const [itemId, datos] of Object.entries(devolucionesAbiertasPorPub)) {
    const ventas = ventasPorPub[itemId] || 0
    if (ventas < volumenMin_prov) continue
    // Sumamos defecto + diferente (ambos son problemas de control de calidad del proveedor).
    const cantDefecto = datos.defecto.length + datos.diferente.length
    if (cantDefecto === 0) continue
    const tasa = cantDefecto / ventas
    if (tasa > umbralProveedor) {
      seccionProveedor.push(buildItem(itemId, pubsMap, {
        _ventas: ventas,
        _defectos: cantDefecto,
        _tasaDefecto: tasa,
        _promedioCuenta: promCuenta_proveedor,
        _umbral: umbralProveedor,
        _excesoSobrePromedio: promCuenta_proveedor > 0 ? (tasa / promCuenta_proveedor - 1) : null,
        metricas: { ventas, defectos: cantDefecto, tasa, umbral: umbralProveedor },
      }))
    }
  }
  seccionProveedor.sort((a, b) => (b._tasaDefecto || 0) - (a._tasaDefecto || 0))

  // ─── SECCIÓN 5 — Cambiar embalaje (tasa damaged > promedio × (1+tol)) ───
  const u_emb = umbrales.cambiar_embalaje || {}
  const tolerancia_emb = Number(u_emb.tolerancia_pct) || 20
  const volumenMin_emb = Number(u_emb.volumen_minimo_devoluciones) || 5
  const umbralEmbalaje = promCuenta_embalaje * (1 + tolerancia_emb / 100)

  const seccionEmbalaje = []
  for (const [itemId, datos] of Object.entries(devolucionesAbiertasPorPub)) {
    const totalDev = datos.todas.length
    if (totalDev < volumenMin_emb) continue
    if (datos.damaged.length === 0) continue
    const tasa = datos.damaged.length / totalDev
    if (tasa > umbralEmbalaje) {
      seccionEmbalaje.push(buildItem(itemId, pubsMap, {
        _devoluciones: totalDev,
        _damaged: datos.damaged.length,
        _tasaDamaged: tasa,
        _promedioCuenta: promCuenta_embalaje,
        _umbral: umbralEmbalaje,
        _excesoSobrePromedio: promCuenta_embalaje > 0 ? (tasa / promCuenta_embalaje - 1) : null,
        metricas: { devoluciones: totalDev, damaged: datos.damaged.length, tasa, umbral: umbralEmbalaje },
      }))
    }
  }
  seccionEmbalaje.sort((a, b) => (b._tasaDamaged || 0) - (a._tasaDamaged || 0))

  // ─── SECCIÓN 6 — Otras problemáticas (residual ambiguo) ───
  // Pubs con reclamo ABIERTO cuyo motivo se identifica como ambiguo
  // (arrepentimiento / diferente / no compatible) y NO es accionable por
  // transportista(envio)/proveedor(defecto)/embalaje(damaged). Se abre CASO y
  // se recategoriza. Conservador: si el motivo no se puede leer, no entra.
  // Mismo criterio que el dashboard: reclamo con publicación cuyo motivo mapea
  // a 'otros' o todavía no está clasificado (mapeoReasonCategoria). Excluye los
  // que ya son informacion/logistica/proveedor/embalaje.
  const CATS_ESPECIFICAS = new Set(['informacion', 'logistica', 'proveedor', 'embalaje'])
  const seccionOtras = []
  for (const [itemId, recs] of Object.entries(reclamosPorPub)) {
    // Descartada por el operador (reversible) → el agente no la sugiere.
    if (dismissedCasos[`otros:${itemId}`]) continue
    const otras = recs.filter(r => {
      // Acotar al período del dashboard (si hay corte): descarta reclamos viejos.
      if (fechaCorteReclamos) {
        const t = new Date(r.dateCreated || r.date_created || 0).getTime()
        if (!t || t < fechaCorteReclamos) return false
      }
      const cat = mapeoReason[r.reasonId] || mapeoReason[r.reason_id] || null
      return !CATS_ESPECIFICAS.has(cat)  // 'otros' o sin clasificar
    })
    if (otras.length === 0) continue
    const incidentes = otras.map(r => ({
      claimId: r.claimId || r.id || null,
      tipo: r.type || r._kind || 'reclamo',
      reasonId: r.reasonId || null,
      motivo: (r.reasonId && mlClaimReasons[r.reasonId]) || r.motivo || null,
      status: r.status || null,
      dateCreated: r.dateCreated || null,
    }))
    seccionOtras.push(buildItem(itemId, pubsMap, {
      _reclamos: otras.length,
      _incidentes: incidentes,
      _motivoTexto: [...new Set(incidentes.map(i => i.motivo).filter(Boolean))].join(' · '),
      _claimId: incidentes[0]?.claimId || null,
      metricas: { reclamosOtros: otras.length },
    }))
  }
  seccionOtras.sort((a, b) => (b._reclamos || 0) - (a._reclamos || 0))

  return {
    analisis: {
      totalReclamos: reclamosTotalCount,
      totalDevoluciones: totalDevolucionesCount,
      totalVentas: ventasTotal,
      totalDefectos: totalDefectoCuenta,
      totalDamaged: totalDamagedCuenta,
      promedioCuenta: {
        pausar: promCuenta_pausar,
        proveedor: promCuenta_proveedor,
        embalaje: promCuenta_embalaje,
      },
      umbralesAplicados: {
        pausar: umbralPausar,
        proveedor: umbralProveedor,
        embalaje: umbralEmbalaje,
      },
    },
    secciones: {
      modificar_publicacion: seccionModificar,
      pausar_publicacion: seccionPausar,
      cambiar_transportista: seccionTransportista,
      cambiar_proveedor: seccionProveedor,
      cambiar_embalaje: seccionEmbalaje,
      otras_problematicas: seccionOtras,
    },
  }
}
